import { z } from 'zod';
import { UuidSchema } from '../_shared/ids.js';
/**
 * Identidad visual de un medio. Es lo mínimo que hace falta para pintar la marca antes de
 * saber quién es el usuario: el login del panel lo necesita (todavía no hay sesión) y el sitio
 * público también.
 *
 * Los dos colores son **solo identidad**: nunca comunican estado. Lo que informa usa el rojo del
 * sistema, que no es configurable.
 *
 * `secondaryColor` viaja acá porque se guarda en el tenant, pero **lo aplica solo el panel**: el
 * sitio público se pinta con una sola variable (`--color-cliente`). Es contención deliberada —
 * abrirlo al sitio sin un uso definido termina en siete lugares con ese color que nadie puede
 * explicar.
 *
 * Los colores salen sin validar a propósito: las filas viejas se cargaron antes de que hubiera
 * un formato, y apretar esto acá haría fallar el `.parse()` de los loaders del sitio. El formato
 * se exige en la escritura (`ActualizarMarcaRequest`).
 */
export const SiteBranding = z
    .object({
    nombre: z.string(),
    slug: z.string(),
    domain: z.string().nullable(),
    logoUrl: z.string().nullable(),
    primaryColor: z.string().nullable(),
    secondaryColor: z.string().nullable(),
})
    .strict();
/**
 * Color en hexadecimal de seis dígitos.
 *
 * Sin la forma corta de tres dígitos: un solo formato guardado evita que cada consumidor tenga
 * que normalizar antes de calcular contraste.
 */
export const HexColor = z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'color en hexadecimal (#RRGGBB)');
export const MARCA_NOMBRE_MAX = 120;
/**
 * Edición de la marca desde el panel: lo que no viene queda como está, y `null` limpia.
 *
 * El logo se manda como `logoMediaId` y no como URL: la URL la deriva el servidor de la media, así
 * que el panel no puede inventar una ni dejar guardada la de otro host.
 *
 * El dominio y el slug **no** están: los administra la plataforma, no el medio.
 */
export const ActualizarMarcaRequest = z
    .object({
    nombre: z.string().trim().min(1).max(MARCA_NOMBRE_MAX).optional(),
    primaryColor: HexColor.nullable().optional(),
    secondaryColor: HexColor.nullable().optional(),
    logoMediaId: UuidSchema.nullable().optional(),
})
    .strict();
export const MARCA_ERROR_CODES = {
    /** El `logoMediaId` no existe en la biblioteca de este medio. */
    logoNoEncontrado: 'MARCA_LOGO_NO_ENCONTRADO',
};
